import { useEffect, useRef } from 'react';

import { useUserDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { GMetrixButtonDropdown } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { UserGMetrixTable } from 'components/UserGMetrixTable/UserGMetrixTable';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { NewUserForm } from 'components/NewUserForm/NewUserForm';
import { User, UsersApi, UserSortField } from 'apis/UsersApi';
import { ButtonGroup, Card, Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState, useUrlFilters } from 'hooks';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { SortDescriptor } from 'utils/dataQuery';
import { KendoIntlProvider } from 'app/culture';
import { currentLookupUrl } from 'utils/other';
import { useUsers } from 'pages/users/context';
import { useNavigate } from 'react-router-dom';
import { helpContent } from 'pages/users/help';
import { useApp } from 'app';

import 'styles/global-styles.scss';

export const SystemAdministrators = () => {

    const navigate = useNavigate();

    const { currentPartnerId, permissions } = useUsers();

    // Persist the view (search, sort, pagination) in the URL query string so navigating away
    // and back restores the same view, then refetch fresh user rows for it.
    const urlFilters = useUrlFilters({
        searchterm: '',
        skip: 0,
        sortdir: '',
        sortfield: '',
        take: 15
    });

    const [state, setState] = useSuperState({
        loadingGridData: true,
        paginationSkip: urlFilters.initial.skip,
        paginationTake: urlFilters.initial.take,
        searchTerm: urlFilters.initial.searchterm,
        serverTotalCount: 0,
        showNewUserModal: false,
        sortDir: urlFilters.initial.sortdir,
        sortField: urlFilters.initial.sortfield as UserSortField | '',
        users: [] as User[]
    });

    const { loadingGridData, paginationSkip, paginationTake, searchTerm, serverTotalCount, showNewUserModal, sortDir, sortField, users } = state;

    const { selectedPartner, userId } = useApp();
    const { formatMessage, locale } = useIntl();
    const userDefaultLookupSearchFields = useUserDefaultLookupSearchFields();
    const { createToast } = useNotification();

    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const isInitialSearchRef = useRef(true);
    const gmetrixConfirmRef = useRef<any>();

    const fetchUsersPaged = async (
        skip = paginationSkip,
        take = paginationTake,
        sField = sortField,
        sDir = sortDir,
        search = searchTerm
    ) => {

        setState({ loadingGridData: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await UsersApi.GetAllPaged(
                page,
                take,
                sField,
                sDir,
                search,
                currentPartnerId ? currentPartnerId : -1
            );
            setState({ serverTotalCount: result.TotalCount, users: result.Items });
            urlFilters.write({
                searchterm: search,
                skip,
                sortdir: sDir,
                sortfield: sField,
                take
            });

        } catch (e) {

            console.error(e);
            const message = typeof e === 'string' ? e : (e as any)?.message || (e as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_user_data_please_try_agai.609f0e4e', defaultMessage: 'Failed to load user data. Please try again.' }), 'danger');

            }

        } finally {

            setState({ loadingGridData: false });

        }

    };

    // Debounce search term changes for server-side pagination (1s delay)
    useEffect(() => {

        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        setState({ loadingGridData: true });

        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchUsersPaged(0, paginationTake, sortField, sortDir, searchTerm);

        }, 1000);

    }, [searchTerm]);

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchUsersPaged(skip, take, sortField, sortDir, searchTerm);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as UserSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchUsersPaged(0, paginationTake, newSortField, newSortDir, searchTerm);

    };

    const getAllUsers = async (partnerId: number) => {

        fetchUsersPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm);

    };

    const handleShowModal = () => setState({ showNewUserModal: true });

    useEffect(() => {

        if (currentPartnerId) {

            // Always refetch on mount, restoring the previous page/sort/search for this partner.
            fetchUsersPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm);

        }

    }, [currentPartnerId]);

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>

                    <PageWithHeader
                        headerConfig={{
                            cta: {
                                className: 'joyride-new-user',
                                disabled: !permissions.UserEdit,
                                label: (
                                    <div className={'d-inline-flex align-items-center gap-1'}>
                                        <i className={'fa-solid fa-plus'} />
                                        <span>
                                            <FormattedMessage defaultMessage="Add User" id="add_user.daaf2592" />
                                        </span>
                                    </div>
                                ),
                                onClick: handleShowModal
                            },
                            helpContent: helpContent,
                            title: (
                                <>
                                    <FormattedMessage defaultMessage="Administrators" description="menu" id="administrators.018ac745" />
                                    {!!selectedPartner?.name && (
                                        <span className="d-block h3 mt-3 mb-0 text-secondary">
                                            {selectedPartner.name}
                                        </span>
                                    )}
                                </>
                            )
                        }}
                    >

                        <div className="system-administrators d-flex flex-column gap-3">

                            <Card body>
                                <UserGMetrixTable
                                    manuallyRenderUsers
                                    serverSidePagination
                                    toggleColumns
                                    columnData={[
                                        { field: 'UserProfileID', sortField: 'ProfileID', title: formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }) },
                                        { field: 'FirstName', sortField: 'firstname', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) },
                                        { field: 'LastName', sortField: 'lastname', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) },
                                        { field: 'Email', sortField: 'EmailAddress', title: formatMessage({ id: 'emailusername.a26a2997', defaultMessage: 'Email/Username' }) },
                                        { field: 'PartnerActive', sortField: 'isActive', title: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) },
                                        { field: 'PartnerInviteText', sortField: 'InviteStatus', title: formatMessage({ id: 'invite_status.28fdfbf1', defaultMessage: 'Invite Status' }) },
                                        {
                                            cell: ({ dataItem }) => (
                                                <td
                                                    className="d-flex justify-content-end"
                                                    role={'gridcell'}
                                                >
                                                    <ButtonGroup>
                                                        <GMetrixButton
                                                            className="joyride-details"
                                                            disabled={dataItem.DisabledDetailsBtn}
                                                            onClick={() => navigate('/administrators/' + dataItem.UserProfileID, { state: { from: currentLookupUrl() } })}
                                                            variant="outline-primary"
                                                        >
                                                            <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                                                        </GMetrixButton>
                                                        <GMetrixButtonDropdown
                                                            dataItems={[
                                                                {
                                                                    action: async () => {

                                                                        if (dataItem.UserProfileID === userId) {

                                                                            createToast(formatMessage({ id: 'you_cannot_remove_your_own_accont.2c7d86f9', defaultMessage: 'You cannot remove your own accont.' }), 'danger');
                                                                            return;

                                                                        }
                                                                        const result = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'remove_user.4584c8fe', defaultMessage: 'Remove User' }), formatMessage({ id: 'are_you_sure_you_want_to_remove_this_use.812e25c4', defaultMessage: 'Are you sure you want to remove this user? There is no undoing this action.' }));
                                                                        if (result) {

                                                                            await UsersApi.RemoveUserFromTC(dataItem.UserProfileID, currentPartnerId);
                                                                            getAllUsers(currentPartnerId);

                                                                        }

                                                                    }, iconClass: 'fa-circle-info text-primary', text: formatMessage({ id: 'delete.2d32914e', defaultMessage: 'Delete' })
                                                                }

                                                            ]}
                                                            className="joyride-details"
                                                            iconClass="fa-solid fa-ellipsis-vertical"
                                                            variant="outline-primary"
                                                        />
                                                    </ButtonGroup>
                                                </td>
                                            ),
                                            field: 'Id',
                                            locked: true,
                                            title: formatMessage({ id: 'actions.61fd1d29', defaultMessage: 'Actions' }),
                                            width: 150
                                        }
                                    ]}
                                    advancedSearchFields={userDefaultLookupSearchFields}
                                    defaultItemsPerPage={paginationTake}
                                    getAllUsers={getAllUsers}
                                    initialSkip={paginationSkip}
                                    loadingGridData={loadingGridData}
                                    onServerPageChange={handleServerPageChange}
                                    onServerSortChange={handleServerSortChange}
                                    searchTerm={searchTerm}
                                    serverTotalCount={serverTotalCount}
                                    setLoadingGridData={val => setState({ loadingGridData: val })}
                                    setSearchTerm={val => setState({ searchTerm: val })}
                                    setUsers={val => setState({ users: val })}
                                    users={users}
                                    viewPermission={permissions.UserView}
                                />
                            </Card>
                        </div>

                    </PageWithHeader>

                    <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

                    <Modal onHide={() => setState({ showNewUserModal: false })} show={showNewUserModal} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title className="w-100">
                                {<FormattedMessage defaultMessage="New Administrator" id="new_administrator.ba99468a" /> || 'New Administrator'}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <NewUserForm
                                onCreated={userProfileId => navigate('/administrators/' + userProfileId, { state: { from: currentLookupUrl() } })}
                                page="User"
                                parentCancel={() => setState({ showNewUserModal: false })}
                                permissions={permissions}
                                setUsers={val => setState({ users: val })}
                            />
                        </Modal.Body>
                    </Modal>

                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
